import { ModelPropertyType } from "@/interfaces";
import { ModelInterface } from "@/interfaces/entities/entity_blueprints/ModelInterface";
import { ModelPropertyInterface } from "@/interfaces/entities/entity_blueprints/ModelPropertyInterface";

type Link = {
  from: string;
  to: string;
  via: string;
};

// Height constants
const FIELD_HEIGHT_WITH_ATTRIBUTES = 100; // Height for fields with required, enum, unique, or regex
const FIELD_HEIGHT_SIMPLE = 70; // Height for simple fields
const BASE_GAP = 200; // Base constant gap between nodes

/**
 * Calculate the height contribution of a single field based on its attributes
 */
function getFieldHeight(prop: ModelPropertyInterface): number {
  const hasSpecialAttributes = 
    prop.required || 
    (prop.enum && prop.enum.length > 0) || 
    prop.unique || 
    prop.match; // match is the regex pattern
  
  return hasSpecialAttributes ? FIELD_HEIGHT_WITH_ATTRIBUTES : FIELD_HEIGHT_SIMPLE;
}

/**
 * Calculate total node height based on all its fields
 */
function calculateNodeHeight(properties: ModelPropertyInterface[]): number {
  const totalFieldsHeight = properties.reduce((sum, prop) => sum + getFieldHeight(prop), 0);
  return totalFieldsHeight + BASE_GAP;
}

export type ConstellationSchema = {
  factTables: string[];
  dimensionTables: string[];
  links: Link[];
  maxFactFields: number;
  maxDimensionFields: number;
  /** Pre-calculated node height per table name - based on field attributes */
  nodeHeights: { [tableName: string]: number };
};

export function generateConstellationSchema(models: ModelInterface[]): ConstellationSchema {
  const referencesMap: Record<string, string[]> = {};
  const incomingCount: Record<string, number> = {};

  // Initialize counts
  for (const model of models) {
    referencesMap[model.name] = [];
    incomingCount[model.name] = 0;
  }

  // Step 1: Build references map
  for (const model of models) {
    for (const prop of model.properties) {
      if (prop.type === ModelPropertyType.Reference && prop.reference) {
        referencesMap[model.name].push(prop.reference);
        incomingCount[prop.reference] = (incomingCount[prop.reference] || 0) + 1;
      }
    }
  }

  // Step 2: Dynamically determine number of fact tables
  const numModels = models.length;
  const numFactTables = Math.ceil((numModels / 25) * 4);

  // Step 3: Sort models by incoming reference count (descending)
  const sortedModels = [...models].sort((a, b) => {
    return (incomingCount[b.name] || 0) - (incomingCount[a.name] || 0);
  });

  // Step 4: Select top N as fact tables, rest as dimension tables
  const factTables: string[] = sortedModels.slice(0, numFactTables).map((m) => m.name);
  const dimensionTables: string[] = sortedModels.slice(numFactTables).map((m) => m.name);

  // Step 5: Build constellation schema JSON
  const links: Link[] = models.flatMap((model) =>
    model.properties
      .filter((p) => p.type === ModelPropertyType.Reference && p.reference)
      .map((p) => ({
        from: model.name,
        to: p.reference!,
        via: p.name,
      }))
  );

  // Step 6: Find max fields AND build nodeHeights map (based on field attributes)
  let maxFactFields = 0;
  let maxDimensionFields = 0;
  const nodeHeights: { [tableName: string]: number } = {};
  
  for (const model of models) {
    // Calculate height based on field attributes
    nodeHeights[model.name] = calculateNodeHeight(model.properties);
    
    if (factTables.includes(model.name)) {
      maxFactFields = Math.max(maxFactFields, model.properties.length);
    }
    if (dimensionTables.includes(model.name)) {
      maxDimensionFields = Math.max(maxDimensionFields, model.properties.length);
    }
  }

  return {
    factTables,
    dimensionTables,
    links,
    maxFactFields,
    maxDimensionFields,
    nodeHeights,
  };
}
  